import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber, formatIndianNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

const SIPCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [calculatorType, setCalculatorType] = useState('sip'); // 'sip' or 'lumpsum'
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);
  
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  
  const [formData, setFormData] = useState({
    monthlyAmount: '5000', // For SIP
    lumpsum: '50000', // For Lumpsum
    rate: '12', // Expected annual return
    time: '10', // Investment period in years
  });

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate SIP or Lumpsum returns
  const calculateReturns = () => {
    if (calculatorType === 'sip') {
      if (!formData.monthlyAmount || !formData.rate || !formData.time || 
          isNaN(formData.monthlyAmount) || isNaN(formData.rate) || isNaN(formData.time)) {
        setResults(null);
        return;
      }

      const monthlyInvestment = parseFloat(formData.monthlyAmount);
      const annualRate = parseFloat(formData.rate);
      const years = parseFloat(formData.time);
      
      const monthlyRate = annualRate / 12 / 100;
      const totalMonths = years * 12;
      
      // SIP Future Value Formula: FV = P * [((1 + r)^n - 1) / r] * (1 + r)
      const futureValue = monthlyInvestment * 
        (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) * (1 + monthlyRate));
      
      const totalInvested = monthlyInvestment * totalMonths;
      const totalReturns = futureValue - totalInvested;

      setResults({
        investedAmount: Math.round(totalInvested),
        expectedReturns: Math.round(totalReturns),
        totalValue: Math.round(futureValue),
        monthlyAmount: monthlyInvestment,
        rate: annualRate,
        time: years,
        calculatorType: 'sip'
      });
    } else {
      // Lumpsum calculation
      if (!formData.lumpsum || !formData.rate || !formData.time || 
          isNaN(formData.lumpsum) || isNaN(formData.rate) || isNaN(formData.time)) {
        setResults(null);
        return;
      }

      const principal = parseFloat(formData.lumpsum);
      const annualRate = parseFloat(formData.rate);
      const years = parseFloat(formData.time);
      
      // Compound Interest Formula: A = P(1 + r/100)^t
      const futureValue = principal * Math.pow(1 + annualRate / 100, years);
      const totalReturns = futureValue - principal;

      setResults({
        investedAmount: Math.round(principal),
        expectedReturns: Math.round(totalReturns),
        totalValue: Math.round(futureValue),
        lumpsum: principal,
        rate: annualRate,
        time: years,
        calculatorType: 'lumpsum'
      });
    }
    setIsCalculated(true);
  };

  // Auto-calculate when inputs change
  useEffect(() => {
    calculateReturns();
  }, [formData, calculatorType]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>SIP Calculator</Text>
        <Text style={styles.heroSubtitle}>
          Calculate your SIP returns and plan your investments with our advanced calculator
        </Text>
      </View>
    </View>
  );

  const renderCalculatorTypeToggle = () => (
    <View style={styles.toggleContainer}>
      <TouchableOpacity
        style={[
          styles.toggleButton,
          calculatorType === 'sip' && styles.toggleButtonActive
        ]}
        onPress={() => setCalculatorType('sip')}
      >
        <Text style={[
          styles.toggleText,
          calculatorType === 'sip' && styles.toggleTextActive
        ]}>SIP</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.toggleButton,
          calculatorType === 'lumpsum' && styles.toggleButtonActive
        ]}
        onPress={() => setCalculatorType('lumpsum')}
      >
        <Text style={[
          styles.toggleText,
          calculatorType === 'lumpsum' && styles.toggleTextActive
        ]}>Lumpsum</Text>
      </TouchableOpacity>
    </View>
  );

  const renderAmountInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>
        {calculatorType === 'sip' ? 'Monthly SIP Amount' : 'Total Investment'}
      </Text>
      <View style={styles.inputContainer}>
        <Text style={styles.currencySymbol}>₹</Text>
        <TextInput
          style={styles.input}
          value={calculatorType === 'sip' ? formData.monthlyAmount : formData.lumpsum}
          onChangeText={(value) => handleInputChange(calculatorType === 'sip' ? 'monthlyAmount' : 'lumpsum', value)}
          placeholder={calculatorType === 'sip' ? '5000' : '50000'}
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
      </View>
    </View>
  );

  const renderRateInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Expected Rate of Return (p.a.)</Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.rate}
          onChangeText={(value) => handleInputChange('rate', value)}
          placeholder="12"
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
        <Text style={styles.percentSymbol}>%</Text>
      </View>
    </View>
  );

  const renderTimeInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>
        {calculatorType === 'sip' ? 'SIP Tenure' : 'Investment Period'} (Years)
      </Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.time}
          onChangeText={(value) => handleInputChange('time', value)}
          placeholder="10"
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
        <Text style={styles.percentSymbol}>Years</Text>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { investedAmount, expectedReturns } = results;
    const total = investedAmount + expectedReturns;
    const investedPercentage = (investedAmount / total) * 100;
    const returnsPercentage = (expectedReturns / total) * 100;

    return (
      <View style={styles.chartContainer}>
        <View style={styles.donutChart}>
          <View style={styles.donutInner}>
            <Text style={styles.donutCenterText}>Total Value</Text>
            <Text style={styles.donutCenterAmount}>₹{formatIndianNumber(total)}</Text>
          </View>
        </View>
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#10B981' }]} />
            <Text style={styles.legendText}>Invested Amount</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: '#3B82F6' }]} />
            <Text style={styles.legendText}>Est. Returns</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsContainer}>
        <View style={styles.panelHeader}>
          <Text style={styles.panelTitle}>Investment Summary</Text>
          <Text style={styles.panelSubtitle}>
            {calculatorType === 'sip' ? 'SIP' : 'Lumpsum'} Investment Results
          </Text>
        </View>

        {renderDonutChart()}

        <View style={styles.resultsList}>
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Expected Returns</Text>
            <Text style={styles.resultValue}>₹{formatIndianNumber(results.expectedReturns)}</Text>
          </View>
          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Invested Amount</Text>
            <Text style={styles.resultValue}>₹{formatIndianNumber(results.investedAmount)}</Text>
          </View>
          <View style={[styles.resultRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total Value</Text>
            <Text style={styles.totalValue}>₹{formatIndianNumber(results.totalValue)}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderSIPInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>Complete Guide to SIP Calculator & Systematic Investment Plan</Text>
      
      <Text style={styles.infoSubtitle}>What is SIP (Systematic Investment Plan)?</Text>
      <Text style={styles.infoText}>
        A Systematic Investment Plan (SIP) is a disciplined investment approach that allows you to invest a fixed amount regularly in mutual funds. Instead of investing a large lump sum, SIP enables you to invest small amounts monthly, quarterly, or at any regular interval. This method harnesses the power of compounding and rupee cost averaging to build substantial wealth over time.
      </Text>
      <Text style={styles.infoText}>
        SIP is particularly beneficial for retail investors who want to participate in equity markets without the stress of timing the market. It's an automated investment strategy that promotes financial discipline and helps achieve long-term financial goals like retirement planning, children's education, or buying a home.
      </Text>

      <Text style={styles.infoSubtitle}>How Does SIP Work? - The Mathematical Formula</Text>
      <Text style={styles.infoText}>
        SIP calculations are based on the compound interest formula. The future value of your SIP investment is calculated using:
      </Text>
      <Text style={styles.infoText}>
        <Text style={{fontWeight: 'bold'}}>FV = P × [((1 + r)^n - 1) / r] × (1 + r)</Text>
      </Text>
      <Text style={styles.infoText}>
        Where: FV = Future Value, P = Monthly SIP Amount, r = Monthly Rate of Return (Annual Rate/12), n = Number of Monthly Installments
      </Text>
      <Text style={styles.infoText}>
        Our SIP calculator uses this precise formula to provide accurate projections of your investment growth, helping you plan your financial future with confidence.
      </Text>

      <Text style={styles.infoSubtitle}>Key Benefits of SIP Investment</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Rupee Cost Averaging:</Text> Automatically buy more units when prices are low and fewer when prices are high, reducing average cost per unit</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Power of Compounding:</Text> Your returns generate their own returns, creating exponential wealth growth over time</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Financial Discipline:</Text> Builds systematic saving and investment habits through automated monthly deductions</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Flexibility & Convenience:</Text> Start with as low as ₹500 per month, modify or pause anytime</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>No Market Timing Required:</Text> Eliminates the guesswork of when to invest, reducing emotional investment decisions</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Goal-Based Investing:</Text> Perfect for achieving specific financial goals with calculated monthly investments</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Tax Benefits:</Text> ELSS SIPs offer tax deductions up to ₹1.5 lakh under Section 80C</Text>
      </View>

      <Text style={styles.infoSubtitle}>SIP vs Lumpsum Investment - Which is Better?</Text>
      <Text style={styles.infoText}>
        <Text style={{fontWeight: 'bold'}}>SIP Investment:</Text> Ideal for regular income earners, provides rupee cost averaging benefits, reduces market volatility impact, and builds disciplined investment habits. Best suited for volatile market conditions and long-term wealth creation.
      </Text>
      <Text style={styles.infoText}>
        <Text style={{fontWeight: 'bold'}}>Lumpsum Investment:</Text> Suitable when you have surplus funds available, works well in consistently rising markets, and can potentially generate higher returns if invested at market lows. Requires better market timing skills.
      </Text>
      <Text style={styles.infoText}>
        Most financial experts recommend SIP for retail investors as it reduces investment risk and provides more predictable returns over the long term.
      </Text>

      <Text style={styles.infoSubtitle}>Types of SIP Plans Available</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Regular SIP:</Text> Fixed monthly investment amount throughout the tenure</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Step-Up SIP:</Text> Gradually increase investment amount annually to counter inflation</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Flexible SIP:</Text> Adjust investment amount based on cash flow and market conditions</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Trigger SIP:</Text> Invest additional amounts when market reaches specific levels</Text>
        <Text style={styles.bulletPoint}>• <Text style={{fontWeight: 'bold'}}>Perpetual SIP:</Text> Continue indefinitely until manually stopped</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use Our Advanced SIP Calculator</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}>Step 1: Select investment type - Choose between SIP (systematic) or Lumpsum (one-time) investment</Text>
        <Text style={styles.stepText}>Step 2: Enter monthly SIP amount - Input the amount you can invest regularly (minimum ₹500)</Text>
        <Text style={styles.stepText}>Step 3: Set expected annual return - Enter realistic return expectations (8-15% for equity funds)</Text>
        <Text style={styles.stepText}>Step 4: Choose investment tenure - Select time period based on your financial goals</Text>
        <Text style={styles.stepText}>Step 5: Analyze results - Review total investment, expected returns, and wealth gained</Text>
        <Text style={styles.stepText}>Step 6: Plan your investment - Use projections to make informed investment decisions</Text>
      </View>

      <Text style={styles.infoSubtitle}>Best Practices for SIP Investment Success</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Start early to maximize compounding benefits - even ₹1000/month for 20 years can create significant wealth</Text>
        <Text style={styles.bulletPoint}>• Choose the right mutual fund category based on risk tolerance and investment horizon</Text>
        <Text style={styles.bulletPoint}>• Increase SIP amount annually by 10-15% to beat inflation and accelerate wealth creation</Text>
        <Text style={styles.bulletPoint}>• Stay invested for long term (minimum 5-7 years) to ride out market volatility</Text>
        <Text style={styles.bulletPoint}>• Review and rebalance portfolio annually but avoid frequent changes</Text>
        <Text style={styles.bulletPoint}>• Use SIP for goal-based investing - retirement, children's education, home purchase</Text>
      </View>

      <Text style={styles.infoSubtitle}>Tax Implications of SIP Investments</Text>
      <Text style={styles.infoText}>
        <Text style={{fontWeight: 'bold'}}>Equity Funds:</Text> Long-term capital gains (>1 year) taxed at 10% above ₹1 lakh annually. Short-term gains taxed at 15%.
      </Text>
      <Text style={styles.infoText}>
        <Text style={{fontWeight: 'bold'}}>Debt Funds:</Text> Long-term gains (>3 years) taxed at 20% with indexation benefits. Short-term gains added to income and taxed as per slab.
      </Text>
      <Text style={styles.infoText}>
        <Text style={{fontWeight: 'bold'}}>ELSS Funds:</Text> Qualify for tax deduction up to ₹1.5 lakh under Section 80C with 3-year lock-in period.
      </Text>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions About SIP Calculator & Investment</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.primary} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What is the minimum amount required to start a SIP?</Text>
            <Text style={styles.faqAnswer}>
              A: Most mutual funds allow you to start a SIP with as low as ₹500 per month. However, some funds may have higher minimum amounts ranging from ₹1,000 to ₹5,000. ELSS funds typically start from ₹500, while some sectoral funds may require ₹1,000 minimum. It's advisable to start with an amount you can comfortably invest regularly without affecting your monthly budget.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: How accurate is this SIP calculator and can I rely on its projections?</Text>
            <Text style={styles.faqAnswer}>
              A: Our SIP calculator uses the standard compound interest formula and provides mathematically accurate projections based on the expected rate of return you input. However, actual returns may vary based on market conditions, fund performance, and economic factors. The calculator assumes a constant rate of return, while real markets are volatile. Use it as a planning tool to estimate potential outcomes, not as a guarantee of returns.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Can I increase or decrease my SIP amount during the investment period?</Text>
            <Text style={styles.faqAnswer}>
              A: Yes, most fund houses offer flexibility to modify your SIP. You can increase your SIP amount through a SIP top-up facility or step-up SIP option. You can also pause your SIP for up to 3 months or stop it anytime without penalties. However, decreasing the amount may require stopping the current SIP and starting a new one with the reduced amount. Some funds also offer flexible SIP options where you can vary the amount monthly.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What happens if I miss a SIP installment due to insufficient funds?</Text>
            <Text style={styles.faqAnswer}>
              A: If you miss 1-2 SIP installments due to insufficient funds in your bank account, it's usually not a major issue. The fund house will typically charge a penalty of ₹25-100 per failed transaction. However, if you miss 3 consecutive installments, your SIP may get cancelled automatically. You can restart it anytime by submitting a fresh SIP form. To avoid this, maintain adequate balance and consider setting up auto-debit a few days after your salary credit.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Is SIP better than lumpsum investment for wealth creation?</Text>
            <Text style={styles.faqAnswer}>
              A: Both have their advantages depending on market conditions and your financial situation. SIP is better for regular income earners, volatile markets, and provides rupee cost averaging benefits. It reduces the impact of market timing and builds disciplined investment habits. Lumpsum works well when you have surplus funds, markets are at lower levels, or during consistently rising markets. For most retail investors, SIP is recommended as it reduces risk and provides more predictable long-term returns.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What is the ideal investment tenure for SIP to maximize returns?</Text>
            <Text style={styles.faqAnswer}>
              A: The ideal SIP tenure depends on your financial goals, but generally, longer tenures yield better results due to the power of compounding. For equity funds, a minimum of 5-7 years is recommended to ride out market volatility. For retirement planning, 15-20 years or more is ideal. For children's education, start when they're young and continue for 10-15 years. The longer you stay invested, the better your chances of achieving higher returns and meeting your financial goals.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Which type of mutual fund is best for SIP investment?</Text>
            <Text style={styles.faqAnswer}>
              A: The best fund type depends on your risk tolerance and investment horizon. For long-term wealth creation (7+ years), large-cap or diversified equity funds are suitable. For higher growth potential with higher risk, consider mid-cap or small-cap funds. For tax saving, choose ELSS funds. For moderate risk, balanced or hybrid funds work well. For short-term goals (1-3 years), debt funds are appropriate. Diversifying across different fund categories can help optimize risk-adjusted returns.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: How does rupee cost averaging work in SIP investments?</Text>
            <Text style={styles.faqAnswer}>
              A: Rupee cost averaging is a key benefit of SIP where you buy more units when the NAV (Net Asset Value) is low and fewer units when NAV is high. This averages out your purchase cost over time, reducing the impact of market volatility. For example, if you invest ₹5,000 monthly and NAV varies between ₹50-100, you'll buy 100 units when NAV is ₹50 and 50 units when NAV is ₹100, resulting in an average cost lower than the arithmetic mean of NAV prices.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Can I start multiple SIPs in different mutual funds simultaneously?</Text>
            <Text style={styles.faqAnswer}>
              A: Yes, you can start multiple SIPs across different mutual funds to diversify your portfolio. This strategy helps spread risk across various fund categories, sectors, and fund houses. You can have SIPs in large-cap, mid-cap, debt funds, and international funds simultaneously. However, ensure you can maintain all SIPs consistently and avoid over-diversification which may dilute returns. Most investors maintain 3-5 SIPs across different categories for optimal diversification.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What are the tax implications of SIP investments and withdrawals?</Text>
            <Text style={styles.faqAnswer}>
              A: Tax treatment depends on the fund type and holding period. For equity funds: Long-term gains (>1 year) are taxed at 10% above ₹1 lakh annually, short-term gains at 15%. For debt funds: Long-term gains (>3 years) are taxed at 20% with indexation, short-term gains added to income. ELSS SIPs offer tax deduction up to ₹1.5 lakh under Section 80C with 3-year lock-in. Dividends from mutual funds are taxable in the hands of investors as per their income tax slab.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: How do I choose the right SIP date and what if it falls on a holiday?</Text>
            <Text style={styles.faqAnswer}>
              A: Choose a SIP date 2-3 days after your salary credit to ensure sufficient balance. Popular dates are 1st, 5th, 10th, 15th, or 25th of the month. If your chosen date falls on a market holiday or weekend, the SIP will be processed on the next working day. Some fund houses allow you to change the SIP date once during the tenure. You can also set up multiple SIP dates in the same fund to benefit from different market levels throughout the month.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What is Step-Up SIP and how does it help in wealth creation?</Text>
            <Text style={styles.faqAnswer}>
              A: Step-Up SIP allows you to increase your SIP amount annually by a predetermined percentage (typically 5-15%). This helps counter inflation and accelerate wealth creation as your income grows. For example, if you start with ₹5,000 monthly SIP with 10% annual step-up, it becomes ₹5,500 in year 2, ₹6,050 in year 3, and so on. This strategy significantly enhances your final corpus compared to regular SIP, as the increased contributions benefit from compounding over the remaining tenure.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    heroSection: {
      backgroundColor: colors.primary,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
    },
    heroContent: {
      maxWidth: 800,
      alignItems: 'center',
    },
    heroTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: 12,
      textAlign: 'center',
    },
    heroSubtitle: {
      fontSize: 16,
      color: '#ffffff',
      textAlign: 'center',
      opacity: 0.9,
      lineHeight: 24,
    },
    calculatorContainer: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    calculatorRow: {
      flexDirection: (isWeb && !isMobile) ? 'row' : 'column',
      gap: 24,
    },
    formContent: {
      flex: 0.7,
      gap: 20,
    },
    summaryPanel: {
      flex: 0.3,
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: 20,
      height: 'fit-content',
      borderWidth: 1,
      borderColor: colors.border,
    },
    toggleContainer: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 4,
      marginBottom: 20,
    },
    toggleButton: {
      flex: 1,
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 6,
      alignItems: 'center',
    },
    toggleButtonActive: {
      backgroundColor: colors.primary,
    },
    toggleText: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.textSecondary,
    },
    toggleTextActive: {
      color: '#ffffff',
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.background,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 16,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginRight: 8,
    },
    percentSymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginLeft: 8,
    },
    input: {
      flex: 1,
      paddingVertical: 16,
      fontSize: 16,
      color: colors.text,
    },
    resultsContainer: {
      gap: 20,
    },
    panelHeader: {
      alignItems: 'center',
      marginBottom: 24,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 4,
    },
    panelSubtitle: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 8,
    },
    chartContainer: {
      alignItems: 'center',
      marginBottom: 20,
    },
    donutChart: {
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: '#10B981',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
      position: 'relative',
    },
    donutInner: {
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: colors.cardBackground,
      justifyContent: 'center',
      alignItems: 'center',
    },
    donutCenterText: {
      fontSize: 10,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    donutCenterAmount: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.text,
      textAlign: 'center',
    },
    chartLegend: {
      flexDirection: 'row',
      gap: 16,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.text,
    },
    resultsList: {
      gap: 12,
    },
    resultRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
    },
    resultLabel: {
      fontSize: 16,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    totalRow: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: 12,
      marginTop: 8,
    },
    totalLabel: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    totalValue: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.primary,
    },
    infoContainer: {
      backgroundColor: colors.cardBackground,
      margin: 20,
      padding: 24,
      borderRadius: 12,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    infoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSubtitle: {
      fontSize: 20,
      fontWeight: '600',
      color: colors.text,
      marginTop: 24,
      marginBottom: 12,
    },
    infoText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
    },
    bulletList: {
      marginTop: 12,
    },
    bulletPoint: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    stepsList: {
      marginTop: 12,
    },
    stepText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    faqContainer: {
      backgroundColor: colors.cardBackground,
      margin: 20,
      borderRadius: 12,
      overflow: 'hidden',
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
      backgroundColor: colors.primary + '10',
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 15,
      color: colors.textSecondary,
      lineHeight: 22,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.formContent}>
            {renderCalculatorTypeToggle()}
            {renderAmountInput()}
            {renderRateInput()}
            {renderTimeInput()}
          </View>
          <View style={styles.summaryPanel}>
            {renderResults()}
          </View>
        </View>
      </View>
      {renderSIPInformation()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

export default SIPCalculatorScreen;